/**
 * Appels et décodage des RPC de la migration 0057 : `gps_historique_info`, `gps_historique`, `gps_rapports` (= `gps_trajets` + `gps_arrets` + `gps_resume` en un appel).
 * Le client est passé en argument (structure minimale) : chaque console garde son propre client, ce module n'en importe aucun.
 * Tout ce qui est décodé est RE-VALIDÉ ici (types, bornes) : un nombre manquant ne devient jamais « 0 km » en silence.
 */
import type { Arret, Historique, InfoHistorique, Position, Resume, Trajet } from './types'

export interface ClientRpc {
  rpc(fn: string, args?: Record<string, unknown>): PromiseLike<{ data: unknown; error: unknown }>
  functions: { invoke(nom: string, options?: { body?: unknown }): PromiseLike<{ data: unknown; error: unknown }> }
}

type Obj = Record<string, unknown>
const obj = (v: unknown): Obj => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Obj) : {})
const nb = (v: unknown, repli = 0): number => {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN
  return Number.isFinite(n) ? n : repli
}
const nbOuNull = (v: unknown): number | null => {
  if (v == null || v === '') return null
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : null
}
const txtOuNull = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v : null)
/** Horodatage ISO → secondes depuis 1970 (décimales conservées). */
const secondes = (v: unknown): number => {
  const ms = typeof v === 'string' ? Date.parse(v) : NaN
  return Number.isFinite(ms) ? ms / 1000 : 0
}
const secondesOuNull = (v: unknown): number | null => (typeof v === 'string' && Number.isFinite(Date.parse(v)) ? Date.parse(v) / 1000 : null)

/* ─────────────────────────────── Décodage ─────────────────────────────── */

export function lireInfo(j: unknown): InfoHistorique {
  const o = obj(j)
  const palier = o.palier === 'pro' || o.palier === 'business' ? o.palier : 'starter'
  return {
    palier,
    gpsDisponible: o.gps_disponible !== false,
    retentionJours: Math.max(1, nb(o.retention_jours, 30)),
    disponibleDepuis: secondes(o.disponible_depuis),
    alertesDisponibles: o.alertes_disponibles === true,
    periodeMaxJours: Math.max(1, nb(o.periode_max_jours, 31)),
    pointsMax: Math.max(10, nb(o.points_max, 5000)),
    pointsDefaut: Math.max(10, nb(o.points_defaut, 1500)),
  }
}

/** `[t, lat, lng, vitesse, cap, contact]` → position. Une ligne illisible est ÉCARTÉE (jamais devinée). */
export function lirePoint(a: unknown): Position | null {
  if (!Array.isArray(a) || a.length < 3) return null
  const t = nbOuNull(a[0])
  const lat = nbOuNull(a[1])
  const lng = nbOuNull(a[2])
  if (t === null || lat === null || lng === null) return null
  return { t, lat, lng, v: nbOuNull(a[3]), cap: nbOuNull(a[4]), ign: a[5] === 1 ? true : a[5] === 0 ? false : null }
}

export function lireHistorique(j: unknown): Historique {
  const o = obj(j)
  const brutPoints = Array.isArray(o.points) ? o.points : []
  const points = brutPoints.map(lirePoint).filter((p): p is Position => p !== null).sort((a, b) => a.t - b.t)
  return {
    du: secondes(o.du),
    au: secondes(o.au),
    retentionJours: nb(o.retention_jours, 30),
    tronqueRetention: o.tronque_retention === true,
    maxPoints: nb(o.max_points, 1500),
    brut: nb(o.brut),
    total: nb(o.total),
    retenus: points.length,
    ecartes: nb(o.ecartes),
    preEchantillonnage: o.pre_echantillonnage === true,
    pauseS: nb(o.pause_s, 120),
    points,
  }
}

export function lireTrajets(lignes: unknown): Trajet[] {
  return (Array.isArray(lignes) ? lignes : []).map((l): Trajet => {
    const o = obj(l)
    return {
      n: nb(o.n), debut: secondes(o.debut), fin: secondes(o.fin), dureeS: nb(o.duree_s), distanceM: nb(o.distance_m),
      vitesseMoyKmh: nbOuNull(o.vitesse_moy_kmh), vitesseMaxKmh: nb(o.vitesse_max_kmh),
      latDebut: nb(o.lat_debut), lngDebut: nb(o.lng_debut), latFin: nb(o.lat_fin), lngFin: nb(o.lng_fin),
      lieuDebut: txtOuNull(o.lieu_debut), lieuFin: txtOuNull(o.lieu_fin),
    }
  })
}

export function lireArrets(lignes: unknown): Arret[] {
  return (Array.isArray(lignes) ? lignes : []).map((l): Arret => {
    const o = obj(l)
    return {
      n: nb(o.n), debut: secondes(o.debut), fin: secondes(o.fin), dureeS: nb(o.duree_s), lat: nb(o.lat), lng: nb(o.lng), lieu: txtOuNull(o.lieu),
      moteurAllume: typeof o.moteur_allume === 'boolean' ? o.moteur_allume : null, moteurAllumeS: nb(o.moteur_allume_s),
    }
  })
}

export function lireResume(j: unknown): Resume {
  const o = obj(j)
  return {
    nbTrajets: nb(o.nb_trajets), nbArrets: nb(o.nb_arrets), distanceM: nb(o.distance_m), conduiteS: nb(o.conduite_s), arretS: nb(o.arret_s),
    arretMoteurAllumeS: nb(o.arret_moteur_allume_s), sansSignalS: nb(o.sans_signal_s), vitesseMaxKmh: nb(o.vitesse_max_kmh),
    vitesseMoyKmh: nbOuNull(o.vitesse_moy_kmh), nbPositions: nb(o.nb_positions),
    premierePosition: secondesOuNull(o.premiere_position), dernierePosition: secondesOuNull(o.derniere_position),
    alertesDisponibles: o.alertes_disponibles === true, nbAlertes: nbOuNull(o.nb_alertes),
    du: secondes(o.du), au: secondes(o.au), retentionJours: nb(o.retention_jours, 30), tronqueRetention: o.tronque_retention === true,
  }
}

/* ─────────────────────────────── Messages d'erreur ─────────────────────────────── */

/** Message lisible d'une erreur de l'historique : jamais le texte brut de la base (ni le nom d'une fonction). */
export function messageErreurHistorique(e: unknown): string {
  const o = obj(e)
  const brut = [o.message, o.details, o.detail].filter((x): x is string => typeof x === 'string').join(' ') || (e instanceof Error ? e.message : String(e))
  const code = typeof o.code === 'string' ? o.code : ''
  if (/periode_trop_longue/.test(brut)) return obj(e).details && typeof obj(e).details === 'string' ? String(obj(e).details) : 'La période demandée est trop longue : 31 jours au plus par demande.'
  if (/periode_invalide/.test(brut)) return 'La fin de la période doit suivre son début.'
  if (/plan_required/.test(brut)) return "Le suivi par boîtier GPS n'est pas disponible pour cette organisation."
  if (/forbidden|permission denied/i.test(brut) || code === '42501') return "Vous n'avez pas accès à l'historique de ce boîtier : il est réservé à l'équipe de l'organisation (dans son dépôt pour un dispatcher limité à un dépôt)."
  if (['42883', 'PGRST202'].includes(code) || /does not exist|schema cache|could not find the function/i.test(brut)) {
    return "L'historique des trajets n'est pas encore activé sur ce serveur (mise à jour en attente)."
  }
  if (/JWT expired|invalid JWT/i.test(brut)) return 'Session expirée — reconnectez-vous.'
  if (/Failed to fetch|NetworkError|Load failed/i.test(brut)) return 'Connexion au serveur impossible. Vérifiez votre accès internet.'
  return brut || "L'historique n'a pas pu être chargé."
}

export class ErreurHistorique extends Error {}

function leverSiErreur(error: unknown): void {
  if (error) throw new ErreurHistorique(messageErreurHistorique(error))
}

/* ─────────────────────────────── Appels ─────────────────────────────── */

const iso = (secondesDepuis1970: number) => new Date(secondesDepuis1970 * 1000).toISOString()

export async function chargerInfo(client: ClientRpc, orgId: string): Promise<InfoHistorique> {
  const { data, error } = await client.rpc('gps_historique_info', { p_org: orgId })
  leverSiErreur(error)
  return lireInfo(data)
}

export async function chargerHistorique(client: ClientRpc, boitierId: string, du: number, au: number, maxPoints: number): Promise<Historique> {
  const { data, error } = await client.rpc('gps_historique', { p_device: boitierId, p_du: iso(du), p_au: iso(au), p_max_points: maxPoints })
  leverSiErreur(error)
  return lireHistorique(data)
}

export interface RapportsBoitier {
  trajets: Trajet[]
  arrets: Arret[]
  resume: Resume
}

/** Réponse de `gps_rapports` : `{ trajets, arrets, resume }`. */
export function lireRapports(j: unknown): RapportsBoitier {
  const o = obj(j)
  return { trajets: lireTrajets(o.trajets), arrets: lireArrets(o.arrets), resume: lireResume(o.resume) }
}

/** Trajets, arrêts et synthèse en UN appel (`gps_rapports` : la base ne segmente qu'une fois). */
export async function chargerRapports(client: ClientRpc, boitierId: string, du: number, au: number): Promise<RapportsBoitier> {
  const { data, error } = await client.rpc('gps_rapports', { p_device: boitierId, p_du: iso(du), p_au: iso(au) })
  leverSiErreur(error)
  return lireRapports(data)
}

/**
 * Adresse d'un point, À LA DEMANDE (fonction `lieux-inverse` : notre serveur de géocodage, sinon le repère le plus proche). Ne lève
 * jamais : « adresse si disponible » — sans réponse, l'écran garde les coordonnées.
 */
export async function chercherAdresse(client: ClientRpc, lat: number, lng: number): Promise<string | null> {
  try {
    const { data, error } = await client.functions.invoke('lieux-inverse', { body: { lat, lng } })
    if (error) return null
    const o = obj(data)
    const nom = txtOuNull(o.nom)
    const sous = txtOuNull(o.sous_titre)
    return nom ? (sous ? `${nom}, ${sous}` : nom) : null
  } catch {
    return null
  }
}

/* ─────────────────────────────── Source de données de l'écran ─────────────────────────────── */

/**
 * Tout ce dont l'écran a besoin, derrière UNE interface : la source réelle (appels à la base) ou la source fictive de l'aperçu
 * (`apercu.ts`, sans réseau). L'écran ne sait pas laquelle il utilise.
 */
export interface SourceHistorique {
  info(): Promise<InfoHistorique>
  historique(boitierId: string, du: number, au: number, maxPoints: number): Promise<Historique>
  rapports(boitierId: string, du: number, au: number): Promise<RapportsBoitier>
  adresse(lat: number, lng: number): Promise<string | null>
}

export function sourceReelle(client: ClientRpc, orgId: string): SourceHistorique {
  return {
    info: () => chargerInfo(client, orgId),
    historique: (id, du, au, max) => chargerHistorique(client, id, du, au, max),
    rapports: (id, du, au) => chargerRapports(client, id, du, au),
    adresse: (lat, lng) => chercherAdresse(client, lat, lng),
  }
}
